import React, { useState } from "react";
import {
  FiBookOpen,
  FiCalendar,
  FiCheck,
  FiCreditCard,
  FiEdit3,
  FiShield,
  FiUser,
  FiX,
  FiChevronDown,
  FiClipboard,
} from "react-icons/fi";
import ChildHeaderCard from "./components/ChildHeaderCard";
import HeaderBack from "./components/HeaderBack";
import ClassParentsSection from "../../../components/ClassParentsSection";
import ClassLinksSection from "../../../components/ClassLinksSection";
import {
  getBirthDateValue,
  formatDate,
  getChildKey,
  maskBsn,
} from "./utils/childUtils";
import {
  getClassroomLabel,
  getClassroomId,
  getCurrentClassroom,
  getTeacherName,
} from "./utils/classroomUtils";
import {
  DEFAULT_PRIVACY_LABELS,
  getEnabledPrivacyCount,
} from "./utils/privacyUtils";
import { PRIVACY_CHANNELS } from "../../../features/privacy/definitions";

const BookIcon = FiBookOpen;
const CalendarIcon = FiCalendar;
const CheckIcon = FiCheck;
const CloseIcon = FiX;
const EditIcon = FiEdit3;
const ShieldIcon = FiShield;
const UserIcon = FiUser;
const CardIcon = FiCreditCard;
const ChevronIcon = FiChevronDown;
const ContactNotesIcon = FiClipboard;

const SectionIcon = ({ children }) => (
  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100/80 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
    {children}
  </span>
);

const DetailRow = ({ icon, label, value }) => (
  <div className="grid grid-cols-[3rem_minmax(0,1fr)] items-start gap-3">
    <SectionIcon>{icon}</SectionIcon>
    <div className="min-w-0 pt-1">
      <p className="m-0 text-[0.88rem] font-semibold leading-tight text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 break-words text-[1rem] font-bold leading-snug text-slate-700 dark:text-slate-200">
        {value || "—"}
      </p>
    </div>
  </div>
);

const SectionCard = ({ icon, title, action, children }) => (
  <section className="rounded-[2rem] border border-white/75 bg-white/90 px-5 py-5 shadow-[0_0.75rem_1.8rem_rgba(15,23,42,0.07)] backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-900/80 dark:shadow-black/20">
    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4">
      <SectionIcon>{icon}</SectionIcon>
      <h2 className="m-0 min-w-0 text-[1.08rem] font-black leading-tight text-slate-950 dark:text-slate-50">
        {title}
      </h2>
      {action}
    </div>

    {children ? <div className="mt-5 grid gap-5 pl-1">{children}</div> : null}
  </section>
);

const ActionButton = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:hover:bg-emerald-900/60 dark:focus-visible:ring-emerald-900"
  >
    {children}
  </button>
);

const ChildProfileView = ({
  t,
  child,
  classroomsByChild,
  privacyByChild,
  pendingChildren = [],
  pendingChildUpdateSet = new Set(),
  onBack,
  onEditPersonal,
  onEditPrivacy,
  onOpenHealth,
}) => {
  const key = getChildKey(child);
  const currentClassroom = getCurrentClassroom(child, classroomsByChild);
  const classroomLabel = getClassroomLabel(child, classroomsByChild, t);
  const classroomId = getClassroomId(currentClassroom);
  const teacherName =
    getTeacherName(currentClassroom) ||
    child?.teacherName ||
    child?.teacherFullName ||
    "";

  const privacyState = privacyByChild?.[key];
  const privacyCount = getEnabledPrivacyCount(privacyState?.data);
  const hasPrivacySettings = !!privacyState?.data;

  const [classExpanded, setClassExpanded] = useState(true);
  const [privacyExpanded, setPrivacyExpanded] = useState(false);

  return (
    <div className="pp-family-view mx-auto grid w-full max-w-[33rem] gap-5 pb-3">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-1 pt-1">
        <HeaderBack
          label={t("common.back", { defaultValue: "Back" })}
          onBack={onBack}
        />

        <ChildHeaderCard
          t={t}
          child={child}
          classroomsByChild={classroomsByChild}
          pendingChildren={pendingChildren}
          pendingChildUpdateSet={pendingChildUpdateSet}
        />
      </div>

      <SectionCard
        icon={<BookIcon className="h-5 w-5" />}
        title={t("parentPortal.child_info.class_title", {
          defaultValue: "Class information",
        })}
        action={
          <ActionButton
            label={
              classExpanded
                ? t("common.collapse", { defaultValue: "Collapse" })
                : t("common.expand", { defaultValue: "Expand" })
            }
            onClick={() => setClassExpanded((current) => !current)}
          >
            <ChevronIcon
              className={`h-5 w-5 transition-transform duration-200 ${
                classExpanded ? "rotate-180" : ""
              }`}
            />
          </ActionButton>
        }
      >
        {classExpanded && (
          <>
            <DetailRow
              icon={<BookIcon className="h-4 w-4" />}
              label={t("parentPortal.class", { defaultValue: "Class" })}
              value={classroomLabel}
            />
            <DetailRow
              icon={<UserIcon className="h-4 w-4" />}
              label={t("parentPortal.teacher", { defaultValue: "Teacher" })}
              value={teacherName || "—"}
            />
            <ClassParentsSection classroomId={classroomId} embedded />
            <ClassLinksSection schoolWide embedded allowManagement={false} />
            <ClassLinksSection classroomId={classroomId} embedded />
          </>
        )}
      </SectionCard>

      <SectionCard
        icon={<EditIcon className="h-5 w-5" />}
        title={t("parentPortal.children.personal_details", {
          defaultValue: "Personal details",
        })}
        action={
          <ActionButton
            label={t("common.actions.edit", { defaultValue: "Edit" })}
            onClick={onEditPersonal}
          >
            <EditIcon className="h-5 w-5" />
          </ActionButton>
        }
      >
        <DetailRow
          icon={<CalendarIcon className="h-4 w-4" />}
          label={t("parentPortal.children.born", { defaultValue: "Born" })}
          value={formatDate(getBirthDateValue(child))}
        />
        <DetailRow
          icon={<CardIcon className="h-4 w-4" />}
          label="BSN"
          value={maskBsn(child?.pendingBsn || child?.bsn)}
        />
      </SectionCard>

      <SectionCard
        icon={<ShieldIcon className="h-5 w-5" />}
        title={t("parentPortal.children.privacy_publication", {
          defaultValue: "Privacy & publication",
        })}
        action={
          <ActionButton
            label={
              hasPrivacySettings
                ? t("common.actions.edit", { defaultValue: "Edit" })
                : t("common.actions.add", { defaultValue: "Add" })
            }
            onClick={onEditPrivacy}
          >
            {hasPrivacySettings ? (
              <EditIcon className="h-5 w-5" />
            ) : (
              <span className="text-[1.8rem] font-light leading-none">+</span>
            )}
          </ActionButton>
        }
      >
        <button
          type="button"
          disabled={!hasPrivacySettings || privacyState?.loading}
          onClick={() => setPrivacyExpanded((current) => !current)}
          className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl text-left disabled:cursor-default"
        >
          <span className="text-[0.92rem] font-semibold leading-relaxed text-slate-600 dark:text-slate-300">
            {privacyState?.loading
              ? t("common.loading", { defaultValue: "Loading..." })
              : hasPrivacySettings
                ? t("parentPortal.children.privacy_count_saved", {
                    defaultValue: "{{count}} preference(s) saved",
                    count: privacyCount,
                  })
                : t("parentPortal.children.no_privacy_preferences", {
                    defaultValue: "No privacy preferences saved yet.",
                  })}
          </span>

          {hasPrivacySettings && (
            <ChevronIcon
              className={`h-5 w-5 text-emerald-700 transition-transform duration-200 ${
                privacyExpanded ? "rotate-180" : ""
              }`}
            />
          )}
        </button>

        {privacyExpanded && hasPrivacySettings && (
          <div className="grid gap-3 border-t border-slate-200/80 pt-4 dark:border-slate-700/80">
            {PRIVACY_CHANNELS.map(({ key: channelKey, labelKey }) => (
              <div
                key={channelKey}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 text-[0.88rem] font-semibold text-slate-600 dark:text-slate-300"
              >
                <span className="min-w-0">
                  {t(labelKey, {
                    defaultValue:
                      DEFAULT_PRIVACY_LABELS[channelKey] || channelKey,
                  })}
                </span>

                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${
                    privacyState.data?.[channelKey]
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {privacyState.data?.[channelKey] ? (
                    <CheckIcon className="h-3.5 w-3.5" />
                  ) : (
                    <CloseIcon className="h-3.5 w-3.5" />
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {typeof onOpenHealth === "function" && (
        <SectionCard
          icon={<ContactNotesIcon className="h-5 w-5" />}
          title={t("parentPortal.health.title", { defaultValue: "Contacts & notes" })}
          action={
            <ActionButton
              label={t("common.open", { defaultValue: "Open" })}
              onClick={onOpenHealth}
            >
              <EditIcon className="h-5 w-5" />
            </ActionButton>
          }
        >
          <p className="m-0 text-[0.92rem] font-semibold leading-relaxed text-slate-600 dark:text-slate-300">
            {t("parentPortal.health.summary_hint", {
              defaultValue: "Doctor, emergency contact and allergies, used for outing forms.",
            })}
          </p>
        </SectionCard>
      )}
    </div>
  );
};

export default ChildProfileView;
